import { useEffect, useRef, useState } from "react";

/**
 * Binary student-landing modes: full hero (scroll locked) or full content sheet.
 * Hero opens only on a NEW upward gesture that begins while already at content top.
 * Arriving at the top mid-fling only pins — it never jumps to hero.
 */
export default function useStudentLandingScroll(enabled) {
  const [scrolled, setScrolled] = useState(false);
  const enterContentRef = useRef(() => {});

  useEffect(() => {
    if (!enabled) {
      enterContentRef.current = () => {};
      return undefined;
    }

    let mode = "hero"; // 'hero' | 'content'
    let locking = false;
    let raf = 0;
    let transitionRaf = 0;
    let touchStartY = 0;
    let touchStartedAtTop = false;

    // Wheel gesture tracking
    let wheelGestureActive = false;
    let wheelStartedAtTop = false;
    let wheelGestureTimer = 0;
    const WHEEL_GESTURE_GAP_MS = 160;
    const ENTER_CONTENT_MS = 780;

    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const cancelTransition = () => {
      if (transitionRaf) {
        window.cancelAnimationFrame(transitionRaf);
        transitionRaf = 0;
      }
      document.body.classList.remove("student-landing-hero-exiting");
      document
        .querySelector(".student-landing")
        ?.classList.remove("is-hero-exiting");
    };

    const getViewportHeight = () =>
      Math.max(
        Math.round(window.visualViewport?.height || window.innerHeight),
        1,
      );

    const getStage = () =>
      document.querySelector(".student-landing .student-hero-stage");

    const setStageHeight = (px) => {
      const stage = getStage();
      if (!stage) return;
      const value = `${Math.max(0, Math.round(px))}px`;
      stage.style.height = value;
      stage.style.minHeight = value;
      stage.style.maxHeight = value;
    };

    const isAtContentTop = () => window.scrollY <= 1;

    // Dialogs that must scroll / accept keys while the landing is in hero lock
    const isInsideLandingOverlay = (target) =>
      Boolean(
        target &&
          typeof target.closest === "function" &&
          target.closest(
            ".resources-menu-overlay, .pending-fee-overlay, .financial-aid-overlay, .bug-report-overlay",
          ),
      );

    const isEditableTarget = (target) => {
      if (!(target instanceof Element)) return false;
      const tag = target.tagName;
      if (tag === "TEXTAREA" || tag === "SELECT") return true;
      if (tag === "INPUT") {
        const type = String(target.getAttribute("type") || "text").toLowerCase();
        return ![
          "button",
          "submit",
          "reset",
          "checkbox",
          "radio",
          "file",
          "image",
          "range",
          "color",
          "hidden",
        ].includes(type);
      }
      return target.isContentEditable;
    };

    const wheelDeltaPx = (e) => {
      if (e.deltaMode === 1) return e.deltaY * 16;
      if (e.deltaMode === 2) return e.deltaY * window.innerHeight;
      return e.deltaY;
    };

    const beginWheelGesture = () => {
      if (!wheelGestureActive) {
        wheelGestureActive = true;
        // Snapshot BEFORE this tick moves scroll — critical for fling vs intentional
        wheelStartedAtTop = isAtContentTop();
      }
      window.clearTimeout(wheelGestureTimer);
      wheelGestureTimer = window.setTimeout(() => {
        wheelGestureActive = false;
        wheelStartedAtTop = false;
      }, WHEEL_GESTURE_GAP_MS);
    };

    const setHeroCssVars = (progress) => {
      const root = document.querySelector(".student-landing");
      const opacity = Math.max(0, 1 - progress * 1.35).toFixed(4);
      const scale = (1.02 + progress * 0.12).toFixed(4);
      const contentScale = (1 - progress * 0.08).toFixed(4);
      const blur = `${(progress * 8).toFixed(2)}px`;
      [document.documentElement, root].forEach((el) => {
        if (!el) return;
        el.style.setProperty("--hero-progress", String(progress));
        el.style.setProperty("--hero-scale", scale);
        el.style.setProperty("--hero-content-opacity", opacity);
        el.style.setProperty("--hero-content-scale", contentScale);
        el.style.setProperty("--hero-blur", blur);
      });
    };

    const applyModeClasses = (next) => {
      const inContent = next === "content";
      const root = document.querySelector(".student-landing");
      root?.classList.toggle("is-scrolled", inContent);
      root?.classList.toggle("is-hero-done", inContent);
      document.body.classList.toggle("student-landing-scrolled", inContent);
      document.body.classList.toggle("student-landing-hero-done", inContent);
      document.body.classList.toggle("student-landing-mode-hero", !inContent);
      document.body.classList.toggle("student-landing-mode-content", inContent);
      setHeroCssVars(inContent ? 1 : 0);
      setScrolled(inContent);
    };

    const lockPageScroll = () => {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.documentElement.style.overscrollBehaviorY = "none";
      document.body.style.overscrollBehaviorY = "none";
    };

    const unlockPageScroll = () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.documentElement.style.overscrollBehaviorY = "none";
      document.body.style.overscrollBehaviorY = "none";
    };

    const clearScrollLocks = () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.documentElement.style.overscrollBehaviorY = "";
      document.body.style.overscrollBehaviorY = "";
    };

    const enterHero = () => {
      if (mode === "hero" && locking) return;
      cancelTransition();
      locking = true;
      mode = "hero";
      wheelGestureActive = false;
      wheelStartedAtTop = false;
      applyModeClasses("hero");
      setStageHeight(getViewportHeight());
      window.scrollTo(0, 0);
      lockPageScroll();
      window.requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        lockPageScroll();
        locking = false;
      });
    };

    const enterContent = () => {
      if (locking || mode === "content") return;
      locking = true;
      mode = "content";
      wheelGestureActive = false;
      wheelStartedAtTop = false;

      const root = document.querySelector(".student-landing");
      const vh = getViewportHeight();
      const reduceMotion = window.matchMedia?.(
        "(prefers-reduced-motion: reduce)",
      )?.matches;
      const duration = reduceMotion ? 0 : ENTER_CONTENT_MS;
      const start = performance.now();

      document.body.classList.add("student-landing-hero-exiting");
      root?.classList.add("is-hero-exiting");

      const tick = (now) => {
        const t = duration <= 0 ? 1 : Math.min(1, (now - start) / duration);
        const p = easeInOutCubic(t);
        setHeroCssVars(p);
        setStageHeight(vh * (1 - p));
        // Switch navbar to solid mid-exit so it doesn't flash pastels late
        if (p >= 0.4) setScrolled(true);

        if (t < 1) {
          transitionRaf = window.requestAnimationFrame(tick);
          return;
        }

        transitionRaf = 0;
        setStageHeight(0);
        unlockPageScroll();
        applyModeClasses("content");
        document.body.classList.remove("student-landing-hero-exiting");
        root?.classList.remove("is-hero-exiting");
        window.scrollTo(0, 0);
        window.requestAnimationFrame(() => {
          window.scrollTo(0, 0);
          locking = false;
        });
      };

      transitionRaf = window.requestAnimationFrame(tick);
    };

    enterContentRef.current = enterContent;

    const clearBodyFlags = () => {
      document.body.classList.remove(
        "student-landing-scrolled",
        "student-landing-hero-done",
        "student-landing-mode-hero",
        "student-landing-mode-content",
      );
      clearScrollLocks();
      setStageHeight(getViewportHeight());
    };

    setStageHeight(getViewportHeight());
    enterHero();

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        if (locking) return;
        if (mode === "hero" && window.scrollY > 0) {
          window.scrollTo(0, 0);
        }
      });
    };

    const onWheel = (e) => {
      // Let landing dialogs scroll while the page is in hero lock
      if (isInsideLandingOverlay(e.target)) return;

      if (mode === "hero") {
        e.preventDefault();
        return;
      }

      beginWheelGesture();

      const delta = wheelDeltaPx(e);

      // Scrolling down — normal
      if (delta > 0) {
        return;
      }

      // Scrolling up
      if (!isAtContentTop()) {
        // Would this tick pass the top? Pin without opening hero.
        if (window.scrollY + delta <= 0) {
          e.preventDefault();
          window.scrollTo(0, 0);
        }
        return;
      }

      // Already at top for this tick
      e.preventDefault();
      window.scrollTo(0, 0);

      // Only enter hero if THIS gesture began while already parked at top
      if (wheelStartedAtTop) {
        enterHero();
      }
    };

    const onTouchStart = (e) => {
      if (e.touches[0]) {
        touchStartY = e.touches[0].clientY;
        touchStartedAtTop = mode === "content" && isAtContentTop();
      }
    };

    const onTouchMove = (e) => {
      if (isInsideLandingOverlay(e.target)) return;

      if (mode === "hero") {
        e.preventDefault();
        return;
      }
      if (!e.touches[0]) return;
      const dy = e.touches[0].clientY - touchStartY;

      // Finger moving down (content pulls down / scroll up)
      if (dy <= 16) return;

      if (!isAtContentTop()) return;

      e.preventDefault();
      window.scrollTo(0, 0);
      if (touchStartedAtTop) {
        enterHero();
      }
    };

    const onKeyDown = (e) => {
      // Don't steal keys from form fields or open landing dialogs (e.g. bug report).
      if (isEditableTarget(e.target) || isInsideLandingOverlay(e.target)) {
        return;
      }

      if (mode !== "hero") {
        if (
          (e.key === "ArrowUp" || e.key === "PageUp" || e.key === "Home") &&
          isAtContentTop()
        ) {
          e.preventDefault();
          // Keyboard: treat each keypress as its own "gesture" at top
          enterHero();
        }
        return;
      }
      const keys = [
        " ",
        "Spacebar",
        "PageDown",
        "PageUp",
        "ArrowDown",
        "ArrowUp",
        "Home",
        "End",
      ];
      if (keys.includes(e.key)) e.preventDefault();
    };

    const onResize = () => {
      if (mode === "hero") {
        setStageHeight(getViewportHeight());
        window.scrollTo(0, 0);
        lockPageScroll();
      } else {
        setStageHeight(0);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(wheelGestureTimer);
      if (raf) window.cancelAnimationFrame(raf);
      cancelTransition();
      enterContentRef.current = () => {};
      clearBodyFlags();
    };
  }, [enabled]);

  return {
    scrolled,
    enterContent: () => enterContentRef.current(),
  };
}
